import { parseCookies } from 'h3'
import { $fetch, FetchError } from 'ofetch'
import type { FetchOptions } from 'ofetch'

const CSRF_COOKIE = 'XSRF-TOKEN'
const CSRF_HEADER = 'X-XSRF-TOKEN'

type FormDataValue = string | Blob | File

// Create a utility function to handle form data conversion
const buildFormData = (method: string, data: Record<string, unknown>): FormData => {
  const formData = new FormData()

  // Add method override for non-POST methods when using FormData
  if (method.toLowerCase() !== 'post') {
    formData.append('_method', method)
  }

  // Helper to parse values correctly
  const parseValue = (value: unknown): FormDataValue => {
    if (typeof value === 'undefined') {
      return ''
    } else if (typeof value === 'boolean') {
      return value ? '1' : '0'
    } else if (value === null) {
      return ''
    } else if (value && typeof value === 'object' && 'getMonth' in value && typeof (value as Date).getMonth === 'function') {
      return (value as Date).toUTCString()
    } else if (value instanceof File || value instanceof Blob) {
      return value
    }
    return String(value)
  }

  // Process data into FormData
  for (const [key, value] of Object.entries(data)) {
    if (Array.isArray(value)) {
      value.forEach(item => formData.append(`${key}[]`, parseValue(item)))
    } else {
      formData.append(key, parseValue(value))
    }
  }

  return formData
}

const baseFetch = $fetch.create({
  credentials: 'include',
  async onRequest({ options }) {
    const { backendUrl, frontendUrl } = useRuntimeConfig().public
    const event = typeof useEvent === 'function' ? useEvent() : null
    let token = event ? parseCookies(event)[CSRF_COOKIE] : useCookie(CSRF_COOKIE).value

    // on client initiate a csrf request and get it from the cookie set by laravel
    if (import.meta.client && ['post', 'delete', 'put', 'patch'].includes(options?.method?.toLowerCase() ?? '')) {
      token = await initCsrf()
    }

    // Check if we need to handle form data
    if (import.meta.client && options.body) {
      // Only run this on client-side
      const data = options.body as Record<string, unknown>

      // Safe check for File instances, avoiding ReferenceError on server
      const hasFile = Object.values(data).some(
        value => value instanceof File || (value && typeof value === 'object' && 'size' in value && 'type' in value && 'name' in value)
      )

      if (hasFile) {
        const originalMethod = options.method || 'POST'
        options.method = 'POST' // Use POST for file uploads
        options.body = buildFormData(originalMethod, data)
      }
    }

    // Start with base headers
    const baseHeaders: Record<string, string> = {
      accept: 'application/json'
    }

    // Add CSRF token if available
    if (token) {
      baseHeaders[CSRF_HEADER] = token
    }

    // Handle server-side cookie headers
    if (import.meta.server) {
      const cookieString = event ? event.headers.get('cookie') : useRequestHeaders(['cookie']).cookie

      if (cookieString) {
        baseHeaders.cookie = cookieString
      }

      baseHeaders.referer = frontendUrl

      // Forward headers for proper analytics and geolocation
      const requestHeaders = useRequestHeaders(['user-agent', 'x-forwarded-for', 'x-real-ip', 'x-forwarded-proto'])

      if (requestHeaders['user-agent']) {
        baseHeaders['user-agent'] = `${requestHeaders['user-agent']} (Nuxt SSR)`
      } else {
        baseHeaders['user-agent'] = 'Nuxt SSR'
      }

      if (requestHeaders['x-forwarded-for']) {
        baseHeaders['x-forwarded-for'] = requestHeaders['x-forwarded-for']
      }

      if (requestHeaders['x-real-ip']) {
        baseHeaders['x-real-ip'] = requestHeaders['x-real-ip']
      }
    }

    baseHeaders['X-Nuxt-Context'] = import.meta.client ? 'client' : 'server'

    // Merge with existing headers (safely convert Headers object if needed)
    if (options.headers) {
      const existingHeaders = options.headers

      // Handle both Headers object and plain object
      if (existingHeaders instanceof Headers) {
        existingHeaders.forEach((value, key) => {
          baseHeaders[key] = value
        })
      } else {
        // Handle plain object
        Object.entries(existingHeaders as Record<string, string>).forEach(([key, value]) => {
          baseHeaders[key] = value
        })
      }
    }

    options.headers = new Headers(baseHeaders)
    options.baseURL = backendUrl
  },
  async onResponseError({ response }) {
    const status = response.status
    if ([500].includes(status)) {
      console.error('[Laravel Error]', response.statusText, response._data)
    }
  }
})

type LarafetchOptions = FetchOptions & {
  // When false, a 401 will not redirect to the login page (e.g. the "am I logged in?" probe).
  redirectIfNotAuthenticated?: boolean
  // Internal: marks the single CSRF retry so we never loop.
  __csrfRetried?: boolean
}

export async function $larafetch<T = unknown>(request: string, options: LarafetchOptions = {}): Promise<T> {
  try {
    return await baseFetch<T>(request, options)
  } catch (error) {
    const status = error instanceof FetchError ? error.response?.status : undefined

    // A stale CSRF token (e.g. a tab left open past the session) → fetch a fresh
    // one and retry once. A 419 means the request was rejected before processing,
    // so retrying is safe (no double-submit).
    if (status === 419 && !options.__csrfRetried) {
      await initCsrf(true)

      return await $larafetch<T>(request, { ...options, __csrfRetried: true })
    }

    // Genuinely unauthenticated (session expired) → send the user to log in
    // instead of letting an uncaught error bubble up.
    if (status === 401 && import.meta.client && options.redirectIfNotAuthenticated !== false) {
      useUser().value = null

      await navigateTo('/login')

      // Halt the caller; we're navigating away.
      return new Promise<never>(() => {})
    }

    throw error
  }
}

export async function initCsrf(force = false) {
  const { backendUrl } = useRuntimeConfig().public
  const existingToken = useCookie(CSRF_COOKIE).value

  if (existingToken && !force) return existingToken

  await $fetch('/auth/csrf-cookie', {
    baseURL: backendUrl,
    credentials: 'include'
  })

  // Re-read the cookie Laravel just set (the existing ref won't pick up a Set-Cookie on its own).
  refreshCookie(CSRF_COOKIE)

  return useCookie(CSRF_COOKIE).value
}
